import React, { useCallback, useEffect, useState } from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import LockIcon from "@mui/icons-material/Lock";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Link as RouterLink } from "react-router";

import {
  listOrgSecrets,
  listProjectSecrets,
  upsertProjectSecret,
  revealProjectSecret,
  deleteProjectSecret,
  type ScopedSecretMetadata,
} from "src/domains/application/module/api/secretsApi";
import { ORGANIZATIONS } from "src/platform/constants/routes";
import { useNotification } from "src/shell/contexts/notificationContext";
import { sectionLabelSx } from "src/shell/styles/themeUtils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProjectSecretsTabProps {
  projectAlias: string;
  orgAlias: string;
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

interface SecretReferenceProps {
  secret: ScopedSecretMetadata;
  onCopy: (label: string, value: string) => void;
}

/**
 * Build an ESO ExternalSecret YAML snippet for a project secret.
 * The snippet references the OpenBao vault path and property so an
 * external app can sync the secret into its namespace via External
 * Secrets Operator.
 */
function buildEsoSnippet(
  vaultPath: string,
  property: string,
  secretKey: string,
): string {
  return `apiVersion: external-secrets.io/v1beta1
kind: ExternalSecret
metadata:
  name: ${secretKey.toLowerCase()}-eso
  namespace: <target-namespace>
spec:
  refreshInterval: 1h
  secretStoreRef:
    kind: ClusterSecretStore
    name: vault-backend
  target:
    name: ${secretKey.toLowerCase()}
    creationPolicy: Owner
  data:
    - secretKey: ${secretKey}
      remoteRef:
        key: ${vaultPath}
        property: ${property}`;
}

function SecretReference({
  secret,
  onCopy,
}: SecretReferenceProps): React.ReactElement {
  const [esoOpen, setEsoOpen] = useState(false);
  const esoSnippet = buildEsoSnippet(
    secret.reference.vaultPath,
    secret.reference.property,
    secret.key,
  );

  return (
    <Stack
      spacing={0.5}
      sx={{
        minWidth: 260,
        maxWidth: 420,
        overflow: "hidden",
      }}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, textTransform: "uppercase" }}
        >
          Vault path
        </Typography>
        <Tooltip title="Copy Vault path">
          <IconButton
            size="small"
            aria-label={`Copy Vault path for ${secret.key}`}
            onClick={() => onCopy("Vault path", secret.reference.vaultPath)}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography
        component="code"
        variant="caption"
        color="primary"
        sx={{ overflowWrap: "anywhere", fontFamily: "monospace" }}
      >
        {secret.reference.vaultPath}
      </Typography>
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 700, textTransform: "uppercase" }}
        >
          Property
        </Typography>
        <Tooltip title="Copy property key">
          <IconButton
            size="small"
            aria-label={`Copy property key ${secret.reference.property}`}
            onClick={() => onCopy("Property key", secret.reference.property)}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography
        component="code"
        variant="caption"
        color="primary"
        sx={{ overflowWrap: "anywhere", fontFamily: "monospace" }}
      >
        {secret.reference.property}
      </Typography>

      {/* ESO ExternalSecret snippet */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
        <Button
          size="small"
          startIcon={<CodeIcon sx={{ fontSize: 16 }} />}
          endIcon={
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transform: esoOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          }
          onClick={() => setEsoOpen((prev) => !prev)}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            color: "text.secondary",
            py: 0.25,
            minHeight: 28,
          }}
        >
          ESO ExternalSecret snippet
        </Button>
        {esoOpen && (
          <Tooltip title="Copy ESO snippet">
            <IconButton
              size="small"
              aria-label={`Copy ESO ExternalSecret snippet for ${secret.key}`}
              onClick={() => onCopy("ESO snippet", esoSnippet)}
            >
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Collapse in={esoOpen}>
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            bgcolor: "action.hover",
            maxHeight: 240,
            overflow: "auto",
          }}
        >
          <Typography
            component="pre"
            variant="caption"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.7rem",
              lineHeight: 1.4,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              m: 0,
            }}
          >
            {esoSnippet}
          </Typography>
        </Paper>
      </Collapse>
    </Stack>
  );
}

function SecretsTableSkeleton({
  scope,
  canManage,
}: {
  scope: "organization" | "project";
  canManage?: boolean;
}): React.ReactElement {
  const isProject = scope === "project";
  const showActions = isProject && canManage;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table
        size="small"
        aria-label={`Loading ${scope} secrets`}
        sx={{ tableLayout: "fixed" }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Key</TableCell>
            <TableCell>Value</TableCell>
            {isProject && <TableCell>Vault reference</TableCell>}
            {!isProject && <TableCell>Scope</TableCell>}
            <TableCell>Last updated</TableCell>
            {showActions && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: 3 }, (_, index) => (
            <TableRow key={index} aria-hidden="true">
              <TableCell>
                <Skeleton variant="text" width={120} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={120} />
              </TableCell>
              <TableCell>
                {isProject ? (
                  <>
                    <Skeleton variant="text" width={260} />
                    <Skeleton variant="text" width={120} />
                  </>
                ) : (
                  <Skeleton variant="text" width={48} />
                )}
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={60} />
              </TableCell>
              {showActions && (
                <TableCell align="right">
                  <Skeleton variant="text" width={96} />
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * ProjectSecretsTab — project-scoped secrets (managed here) plus a read-only
 * view of org-inherited secrets. Project secrets are inherited by all stacks
 * in the project; org secrets also apply. Does not require any stack to exist.
 */
export function ProjectSecretsTab({
  projectAlias,
  orgAlias,
}: ProjectSecretsTabProps): React.ReactElement {
  const theme = useTheme();
  const { showNotification } = useNotification();
  // The JWT is in an httpOnly cookie and cannot be decoded client-side.
  // UI visibility is advisory only. The API remains the authorization source
  // of truth for every secret operation, so we default to showing management
  // controls and let the API reject unauthorized requests.
  const canManage = true;

  // Inherited org secrets (read-only)
  const [orgSecrets, setOrgSecrets] = useState<ScopedSecretMetadata[]>([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgError, setOrgError] = useState<string | null>(null);

  // Project secrets (CRUD)
  const [secrets, setSecrets] = useState<ScopedSecretMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealLoading, setRevealLoading] = useState<Set<string>>(new Set());

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string>("");
  const [formData, setFormData] = useState({ key: "", value: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleCopy = useCallback(
    async (label: string, value: string) => {
      try {
        await navigator.clipboard.writeText(value);
        showNotification(`${label} copied`, "success");
      } catch {
        showNotification(`Could not copy ${label.toLowerCase()}`, "error");
      }
    },
    [showNotification],
  );

  const mergeSecret = useCallback((secret: ScopedSecretMetadata) => {
    setSecrets((current) => {
      const next = current.filter((item) => item.key !== secret.key);
      return [...next, secret].sort((a, b) => a.key.localeCompare(b.key));
    });
  }, []);

  const fetchOrgSecrets = useCallback(async () => {
    setOrgLoading(true);
    setOrgError(null);
    try {
      const data = await listOrgSecrets(orgAlias);
      setOrgSecrets(data);
    } catch (err) {
      setOrgError(
        err instanceof Error ? err.message : "Failed to load org secrets",
      );
    } finally {
      setOrgLoading(false);
    }
  }, [orgAlias]);

  const fetchSecrets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listProjectSecrets(projectAlias);
      setSecrets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load secrets");
    } finally {
      setLoading(false);
    }
  }, [projectAlias]);

  useEffect(() => {
    void fetchOrgSecrets();
  }, [fetchOrgSecrets]);

  useEffect(() => {
    void fetchSecrets();
  }, [fetchSecrets]);

  const isVaultUnreachable =
    (error !== null && error.includes("Vault is unreachable")) ||
    (orgError !== null && orgError.includes("Vault is unreachable"));

  const handleReveal = async (key: string) => {
    if (revealed[key]) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setRevealLoading((prev) => new Set(prev).add(key));
    try {
      const value = await revealProjectSecret(projectAlias, key);
      setRevealed((prev) => ({ ...prev, [key]: value }));
    } catch (err) {
      showNotification(
        `Failed to reveal secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setRevealLoading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleAdd = () => {
    setFormData({ key: "", value: "" });
    setAddDialogOpen(true);
  };

  const handleEdit = (secret: ScopedSecretMetadata) => {
    setActiveKey(secret.key);
    setFormData({ key: secret.key, value: "" });
    setEditDialogOpen(true);
  };

  const handleDelete = (key: string) => {
    setActiveKey(key);
    setDeleteDialogOpen(true);
  };

  const submitAdd = async () => {
    if (!formData.key.trim() || !formData.value.trim()) return;
    setSubmitting(true);
    try {
      const createdSecret = await upsertProjectSecret(
        projectAlias,
        formData.key.trim(),
        formData.value,
      );
      mergeSecret(createdSecret);
      showNotification("Secret added successfully", "success");
      setAddDialogOpen(false);
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to add secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (!formData.value.trim()) return;
    setSubmitting(true);
    try {
      const updatedSecret = await upsertProjectSecret(
        projectAlias,
        activeKey,
        formData.value,
      );
      mergeSecret(updatedSecret);
      showNotification("Secret updated successfully", "success");
      setEditDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to update secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    setSubmitting(true);
    try {
      await deleteProjectSecret(projectAlias, activeKey);
      showNotification("Secret deleted successfully", "success");
      setDeleteDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to delete secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const orgDetailsHref = `${ORGANIZATIONS.DETAILS.getPath(orgAlias)}?tab=secrets`;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Inheritance banner */}
      <Alert
        severity="info"
        icon={<LinkIcon fontSize="small" />}
        sx={{ borderLeft: `3px solid ${theme.palette.primary.main}` }}
      >
        These secrets are inherited by{" "}
        <strong>all stacks in this project</strong>. Organization-level secrets
        also apply. A stack secret with the same key overrides these.
      </Alert>

      {/* Inherited from organization (read-only) */}
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Inherited from Organization
          </Typography>
          <Button
            size="small"
            component={RouterLink}
            to={orgDetailsHref}
            endIcon={<ArrowForwardIcon fontSize="small" />}
            sx={{ textTransform: "none" }}
          >
            Manage at org level
          </Button>
        </Stack>

        {orgLoading ? (
          <SecretsTableSkeleton scope="organization" />
        ) : orgError ? (
          isVaultUnreachable ? (
            <Alert
              severity="warning"
              icon={false}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => void fetchOrgSecrets()}
                >
                  Try again
                </Button>
              }
            >
              Can&apos;t reach the secret store (Vault). This may be a temporary
              issue. Click retry to try again.
            </Alert>
          ) : (
            <Alert severity="error">{orgError}</Alert>
          )
        ) : orgSecrets.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No organization secrets. Org admins can add secrets that are
              inherited by every project and stack in the org.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Last updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orgSecrets.map((secret) => (
                  <TableRow
                    key={secret.key}
                    sx={{
                      "&:hover": { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={600}
                      >
                        {secret.key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        color="text.secondary"
                      >
                        ••••••••••••••••
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label="org" size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{relativeTime(secret.updatedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Project secrets (managed here) */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            Project Secrets
          </Typography>
          <Typography variant="body2" color="text.secondary">
            — shared by all stacks in this project
          </Typography>
          {secrets.length > 0 && (
            <Chip label={secrets.length} size="small" variant="outlined" />
          )}
          <Box sx={{ flex: 1 }} />
          {canManage && (
            <Button variant="contained" size="small" onClick={handleAdd}>
              Add Secret
            </Button>
          )}
          {!canManage && (
            <Typography variant="caption" color="text.secondary">
              Read-only access. Project write access is required to manage
              secrets.
            </Typography>
          )}
        </Stack>

        {loading ? (
          <SecretsTableSkeleton scope="project" canManage={canManage} />
        ) : error ? (
          isVaultUnreachable ? (
            <Alert
              severity="warning"
              icon={false}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => void fetchSecrets()}
                >
                  Try again
                </Button>
              }
            >
              Can&apos;t reach the secret store (Vault). This may be a temporary
              issue. Click retry to try again.
            </Alert>
          ) : (
            <Alert severity="error">{error}</Alert>
          )
        ) : secrets.length === 0 ? (
          <Paper
            data-testid="project-secrets-empty-state"
            variant="outlined"
            sx={{ p: "20px" }}
          >
            <Box
              data-testid="project-secrets-empty-content"
              sx={{
                textAlign: "center",
                p: "48px 24px",
                color: "text.secondary",
              }}
            >
              <Box
                aria-hidden="true"
                sx={{
                  mb: "16px",
                  opacity: 0.9,
                  "& svg": { maxWidth: "180px", height: "auto" },
                }}
              >
                <svg viewBox="0 0 180 160" width="180" role="presentation">
                  <rect
                    x="50"
                    y="40"
                    width="80"
                    height="100"
                    rx="8"
                    fill={alpha(theme.palette.primary.main, 0.06)}
                    stroke={alpha(theme.palette.primary.main, 0.4)}
                    strokeWidth="2"
                  />
                  <rect
                    x="62"
                    y="56"
                    width="56"
                    height="10"
                    rx="3"
                    fill={alpha(theme.palette.primary.main, 0.5)}
                  />
                  <rect
                    x="62"
                    y="74"
                    width="56"
                    height="6"
                    rx="3"
                    fill={alpha(theme.palette.primary.main, 0.25)}
                  />
                  <rect
                    x="62"
                    y="88"
                    width="56"
                    height="6"
                    rx="3"
                    fill={alpha(theme.palette.primary.main, 0.25)}
                  />
                  <rect
                    x="62"
                    y="102"
                    width="40"
                    height="6"
                    rx="3"
                    fill={alpha(theme.palette.primary.main, 0.25)}
                  />
                  <circle
                    cx="120"
                    cy="120"
                    r="14"
                    fill={alpha(theme.palette.primary.main, 0.9)}
                  />
                  <rect
                    x="115"
                    y="115"
                    width="10"
                    height="10"
                    rx="2"
                    fill={theme.palette.common.white}
                  />
                </svg>
              </Box>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "text.primary",
                  mb: "8px",
                }}
              >
                No project secrets yet
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  color: "text.secondary",
                  maxWidth: "420px",
                  mx: "auto",
                  mb: "16px",
                  lineHeight: 1.6,
                }}
              >
                Add a secret to make it available to every stack in this
                project. The value stays protected while its Vault path and
                property key remain available as safe references.
              </Typography>
              {canManage && (
                <Button variant="contained" onClick={handleAdd}>
                  Add Secret
                </Button>
              )}
            </Box>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Vault reference</TableCell>
                  <TableCell>Last updated</TableCell>
                  {canManage && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {secrets.map((secret) => (
                  <TableRow
                    key={secret.key}
                    sx={{
                      "&:hover": { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        fontWeight={600}
                      >
                        {secret.key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontFamily="monospace"
                        color="text.secondary"
                      >
                        {revealed[secret.key] ?? "••••••••••••••••"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <SecretReference
                        secret={secret}
                        onCopy={(label, value) => void handleCopy(label, value)}
                      />
                    </TableCell>
                    <TableCell>{relativeTime(secret.updatedAt)}</TableCell>
                    {canManage && (
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="flex-end"
                        >
                          <Tooltip
                            title={
                              revealed[secret.key]
                                ? "Hide value"
                                : "Reveal value"
                            }
                          >
                            <IconButton
                              size="small"
                              aria-label={
                                revealed[secret.key]
                                  ? `Hide ${secret.key}`
                                  : `Reveal ${secret.key}`
                              }
                              onClick={() => handleReveal(secret.key)}
                              disabled={revealLoading.has(secret.key)}
                            >
                              {revealLoading.has(secret.key) ? (
                                <CircularProgress size={16} />
                              ) : revealed[secret.key] ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              aria-label={`Edit ${secret.key}`}
                              onClick={() => handleEdit(secret)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              aria-label={`Delete ${secret.key}`}
                              onClick={() => handleDelete(secret.key)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add Secret Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockIcon fontSize="small" color="action" />
            Add Project Secret
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Secret Key"
            placeholder="DATABASE_URL"
            value={formData.key}
            onChange={(e) =>
              setFormData((p) => ({ ...p, key: e.target.value }))
            }
            fullWidth
            inputProps={{ pattern: "^[A-Z][A-Z0-9_]*$" }}
            helperText="UPPER_SNAKE_CASE. Inherited by all stacks in this project."
          />
          <TextField
            margin="dense"
            label="Secret Value"
            placeholder="Enter secret value..."
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Encrypted at rest in Vault. Never shown in list view."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAdd}
            disabled={
              submitting || !formData.key.trim() || !formData.value.trim()
            }
          >
            {submitting ? <CircularProgress size={20} /> : "Add Secret"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Secret Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Project Secret</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Secret Key"
            value={activeKey}
            disabled
            fullWidth
          />
          <TextField
            autoFocus
            margin="dense"
            label="New Secret Value"
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Enter the new value. The previous value will be overwritten."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitEdit}
            disabled={submitting || !formData.value.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Secret</DialogTitle>
        <DialogContent>
          <Typography>
            Delete secret{" "}
            <strong style={{ fontFamily: "monospace" }}>{activeKey}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. All stacks inheriting it will lose
            this env var on the next deploy.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={submitDelete}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProjectSecretsTab;
